<template>
  <div class="kv-ve-spacing">
    <!-- Simple mode: 2 fields (Y and X) -->
    <template v-if="!isCustomMode">
      <div class="kv-ve-spacing__simple">
        <div class="kv-ve-spacing__field">
          <label class="kv-ve-spacing__field-label">{{ yLabelLocalized }}</label>
          <input
            type="number"
            class="kv-ve-spacing__input"
            :value="simpleY"
            min="0"
            @input="setSimpleY($event.target.value)"
          />
        </div>
        <div class="kv-ve-spacing__field">
          <label class="kv-ve-spacing__field-label">{{ xLabelLocalized }}</label>
          <input
            type="number"
            class="kv-ve-spacing__input"
            :value="simpleX"
            min="0"
            @input="setSimpleX($event.target.value)"
          />
        </div>
        <!-- Cog button to enter custom mode -->
        <button
          type="button"
          class="kv-ve-spacing__btn kv-ve-spacing__btn--cog"
          :class="{ active: isCustomMode }"
          :title="t('visual_editor-custom_settings')"
          @click="toggleCustomMode"
        >
          <Icon icon-class="ui-settings-02" size="4" />
        </button>
      </div>
    </template>

    <!-- Custom mode: 4 individual fields -->
    <template v-else>
      <div class="kv-ve-spacing__custom">
        <!-- Presets row (always visible on top) -->
        <div class="kv-ve-spacing__presets">
          <button
            type="button"
            class="kv-ve-spacing__btn kv-ve-spacing__btn--cog"
            :class="{ active: true }"
            :title="t('visual_editor-back_to_simple_mode')"
            @click="toggleCustomMode"
          >
            <Icon icon-class="ui-settings-02" size="4" />
          </button>
        </div>

        <!-- 4 individual fields using grid layout like VeBoxSpacingPicker -->
        <div class="kv-ve-spacing__grid">
          <input
            class="kv-ve-spacing__input kv-ve-spacing__input--top"
            type="number"
            :min="min"
            :max="max"
            :step="step"
            :value="customValues.top"
            :aria-label="t('visual_editor-top')"
            @input="setSide('top', $event.target.value)"
          />
          <input
            class="kv-ve-spacing__input kv-ve-spacing__input--right"
            type="number"
            :min="min"
            :max="max"
            :step="step"
            :value="customValues.right"
            :aria-label="t('visual_editor-right')"
            @input="setSide('right', $event.target.value)"
          />
          <input
            class="kv-ve-spacing__input kv-ve-spacing__input--bottom"
            type="number"
            :min="min"
            :max="max"
            :step="step"
            :value="customValues.bottom"
            :aria-label="t('visual_editor-bottom')"
            @input="setSide('bottom', $event.target.value)"
          />
          <input
            class="kv-ve-spacing__input kv-ve-spacing__input--left"
            type="number"
            :min="min"
            :max="max"
            :step="step"
            :value="customValues.left"
            :aria-label="t('visual_editor-left')"
            @input="setSide('left', $event.target.value)"
          />

          <div class="kv-ve-spacing__box" aria-hidden="true">
            <div class="kv-ve-spacing__box-inner">{{ centerLabel }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Icon from "@/components/icon/icon.vue";

export default {
  name: "VeSpacingPicker",
  components: { Icon },
  model: { prop: "value", event: "input" },
  props: {
    value: {
      type: Object,
      default: () => ({ top: 0, right: 0, bottom: 0, left: 0 }),
    },
    centerLabel: { type: String, default: "" },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 999 },
    step: { type: Number, default: 1 },
  },
  data() {
    return {
      isCustomMode: false,
      customValues: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    };
  },
  computed: {
    yLabelLocalized() {
      return this.t("visual_editor-vertical");
    },
    xLabelLocalized() {
      return this.t("visual_editor-horizontal");
    },
    simpleY() {
      // In simple mode, Y = top = bottom
      const v = this.value || {};
      if (this.isCustomMode) {
        return this.customValues.top === this.customValues.bottom ? this.customValues.top : "-";
      }
      const top = Number(v.top) || 0;
      const bottom = Number(v.bottom) || 0;
      return top === bottom ? top : "-";
    },
    simpleX() {
      // In simple mode, X = left = right
      const v = this.value || {};
      if (this.isCustomMode) {
        return this.customValues.left === this.customValues.right ? this.customValues.left : "-";
      }
      const left = Number(v.left) || 0;
      const right = Number(v.right) || 0;
      return left === right ? left : "-";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        const v = newVal || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) && x >= 0 ? Math.round(x) : 0;
        };
        const top = safe(v.top);
        const right = safe(v.right);
        const bottom = safe(v.bottom);
        const left = safe(v.left);

        // Check if values are different (custom mode needed)
        const isCustom = top !== bottom || left !== right;

        if (isCustom && !this.isCustomMode) {
          // Enter custom mode if values differ
          this.isCustomMode = true;
        }

        // Update custom values
        if (
          this.customValues.top !== top ||
          this.customValues.right !== right ||
          this.customValues.bottom !== bottom ||
          this.customValues.left !== left
        ) {
          this.customValues = { top, right, bottom, left };
        }
      },
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    toggleCustomMode() {
      if (this.isCustomMode) {
        // Exit custom mode: convert to simple (use top/left as Y/X)
        const y = this.customValues.top;
        const x = this.customValues.left;
        this.isCustomMode = false;
        this.emitValue({ top: y, right: x, bottom: y, left: x });
      } else {
        // Enter custom mode: initialize from current value
        const v = this.value || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) && x >= 0 ? Math.round(x) : 0;
        };
        this.customValues = {
          top: safe(v.top),
          right: safe(v.right),
          bottom: safe(v.bottom),
          left: safe(v.left),
        };
        this.isCustomMode = true;
      }
    },
    setSimpleY(val) {
      const n = Math.max(this.min, Math.min(this.max, Math.round(Number(val) || 0)));
      const v = this.value || {};
      const x = Number(v.left) || Number(v.right) || 0;
      this.emitValue({ top: n, right: x, bottom: n, left: x });
    },
    setSimpleX(val) {
      const n = Math.max(this.min, Math.min(this.max, Math.round(Number(val) || 0)));
      const v = this.value || {};
      const y = Number(v.top) || Number(v.bottom) || 0;
      this.emitValue({ top: y, right: n, bottom: y, left: n });
    },
    setSide(side, raw) {
      const n = Math.max(this.min, Math.min(this.max, Math.round(Number(raw) || 0)));
      this.customValues[side] = n;
      this.emitValue({ ...this.customValues });
    },
    emitValue(val) {
      this.$emit("input", { ...val });
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-spacing {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Simple mode: 2 fields + cog */
.kv-ve-spacing__simple {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.kv-ve-spacing__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.kv-ve-spacing__simple .kv-ve-spacing__field .kv-ve-spacing__field-label {
  font-size: 10px;
  font-weight: 400;
  color: #667085;
  margin: 0;
}
 .kv-ve-spacing__input {
  width: 100%;
  height: 34px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-top: 0px;
  padding: 0 10px;
  font-size: 13px;
  color: #101828;
  font-variant-numeric: tabular-nums;
  outline: none;
}
.kv-ve-spacing__input:focus {
  border-color: rgba(42, 170, 76, 0.65);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}
.kv-ve-spacing__input::-webkit-inner-spin-button,
.kv-ve-spacing__input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.kv-ve-spacing__input[type="number"] {
  -moz-appearance: textfield;
}

.kv-ve-spacing__btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.12s ease;
  flex: 0 0 auto;
}
.kv-ve-spacing__btn:hover:not(:disabled) {
  background: rgba(42, 170, 76, 0.08);
  border-color: rgba(42, 170, 76, 0.35);
}
.kv-ve-spacing__btn.active {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.14);
  background: rgba(42, 170, 76, 0.10);
}
.kv-ve-spacing__btn--cog {
  color: #344054;
}
.kv-ve-spacing__btn--cog.active {
  color: #027a48;
}

/* Custom mode */
.kv-ve-spacing__custom {
  display: flex;
  flex-direction: column;
  margin-top: -24px;
  gap: 12px;
}
.kv-ve-spacing__presets {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.kv-ve-spacing__grid {
  display: grid;
  grid-template-columns: 64px 1fr 64px;
  grid-template-rows: 32px 74px 32px;
  grid-template-areas:
    ". top ."
    "left box right"
    ". bottom .";
  align-items: center;
  gap: 8px;
}
.kv-ve-spacing__input--top {
  grid-area: top;
  justify-self: center;
}
.kv-ve-spacing__input--right {
  grid-area: right;
  justify-self: end;
}
.kv-ve-spacing__input--bottom {
  grid-area: bottom;
  justify-self: center;
}
.kv-ve-spacing__input--left {
  grid-area: left;
  justify-self: start;
}
.kv-ve-spacing__box {
  grid-area: box;
  height: 74px;
  border: 1px dashed #d0d5dd;
  border-radius: 14px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.kv-ve-spacing__box-inner {
  font-size: 11px;
  font-weight: 500;
  color: #667085;
}
</style>


